/**
 * InterviewTemplateManagement — Admin templates d'entretien (v4.4.0)
 * CRUD templates avec éditeur de questions structurées
 * Drag & drop + flèches + numéro d'ordre + prévisualisation + sections avec description
 * Dupliquer template + durée estimée + compteur questions
 */

import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, GripVertical, X, ClipboardList, ChevronUp, ChevronDown, Eye, Copy, Clock, Star } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getInterviewTemplates, createInterviewTemplate, updateInterviewTemplate, deleteInterviewTemplate } from '../../api';
import { useToast } from '../../contexts/NotificationContext';
import Modal from '../ui/Modal';

const ROUND_LABELS = { 1: 'Round 1 — Telephonique', 2: 'Round 2 — Presentiel', 3: 'Round 3 — Visite structure' };
const QUESTION_TYPES = [
  { value: 'section', label: '── Section (titre)' },
  { value: 'text', label: 'Texte court' },
  { value: 'textarea', label: 'Texte long' },
  { value: 'rating', label: 'Note (1-5)' },
  { value: 'radio', label: 'Choix unique' },
  { value: 'checkbox', label: 'Cases a cocher' },
];

const emptyQuestion = () => ({ id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, label: '', type: 'text', options: [], required: false, description: '', duration: '' });

// ─── Composant question sortable ─────────────────────────────────────────────
const SortableQuestion = ({ q, idx, total, onUpdate, onRemove, onMove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: q.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className={`bg-gray-50 rounded-lg p-3 ${isDragging ? 'ring-2 ring-indigo-300' : ''}`}>
      <div className="flex items-start gap-2">
        {/* Drag handle + flèches + numéro */}
        <div className="flex flex-col items-center mt-1 w-6 gap-0.5">
          <button type="button" {...attributes} {...listeners} className="cursor-grab text-gray-300 hover:text-gray-500 active:cursor-grabbing">
            <GripVertical size={14} />
          </button>
          <button type="button" onClick={() => onMove(idx, -1)} disabled={idx === 0}
            className={`p-0.5 rounded ${idx === 0 ? 'text-gray-200' : 'text-gray-400 hover:text-indigo-600'}`}>
            <ChevronUp size={10} />
          </button>
          <span className="text-[9px] text-gray-400 font-mono">{idx + 1}</span>
          <button type="button" onClick={() => onMove(idx, 1)} disabled={idx === total - 1}
            className={`p-0.5 rounded ${idx === total - 1 ? 'text-gray-200' : 'text-gray-400 hover:text-indigo-600'}`}>
            <ChevronDown size={10} />
          </button>
        </div>

        {/* Contenu question */}
        <div className="flex-1 space-y-2">
          <input type="text" value={q.label} onChange={e => onUpdate(idx, 'label', e.target.value)}
            className={`w-full border rounded px-2 py-1.5 text-sm ${q.type === 'section' ? 'font-bold bg-indigo-50 border-indigo-200' : ''}`}
            placeholder={q.type === 'section' ? 'Titre de la section...' : 'Question...'} />

          {/* Description (pour sections et questions) */}
          {(q.type === 'section' || q.description) && (
            <textarea value={q.description || ''} onChange={e => onUpdate(idx, 'description', e.target.value)}
              className="w-full border rounded px-2 py-1 text-xs text-gray-500 resize-none" rows={2}
              placeholder={q.type === 'section' ? 'Instructions pour l\'intervieweur...' : 'Precisions sur la question...'} />
          )}
          {q.type !== 'section' && !q.description && (
            <button type="button" onClick={() => onUpdate(idx, 'description', ' ')}
              className="text-[10px] text-gray-400 hover:text-indigo-500">+ Ajouter une description</button>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <select value={q.type} onChange={e => onUpdate(idx, 'type', e.target.value)} className="border rounded px-2 py-1 text-xs">
              {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {q.type !== 'section' && (
              <label className="flex items-center gap-1 text-xs text-gray-500">
                <input type="checkbox" checked={q.required} onChange={e => onUpdate(idx, 'required', e.target.checked)} />
                Obligatoire
              </label>
            )}
            {q.type === 'section' && (
              <input type="text" value={q.duration || ''} onChange={e => onUpdate(idx, 'duration', e.target.value)}
                className="border rounded px-2 py-1 text-xs w-24" placeholder="Duree (ex: 5 min)" />
            )}
          </div>

          {/* Options pour radio/checkbox */}
          {(q.type === 'radio' || q.type === 'checkbox') && (
            <div>
              <div className="flex flex-wrap gap-1 mb-1">
                {(q.options || []).map((opt, oi) => (
                  <span key={oi} className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-50 text-violet-700 rounded-full text-xs">
                    {opt}
                    <button type="button" onClick={() => onUpdate(idx, 'options', q.options.filter((_, i) => i !== oi))} className="text-violet-400 hover:text-red-500">
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1">
                <input type="text" className="flex-1 border rounded px-2 py-1 text-xs" placeholder="Nouvelle option..."
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = e.target.value.trim();
                      if (val) { onUpdate(idx, 'options', [...(q.options || []), val]); e.target.value = ''; }
                    }
                  }} />
                <button type="button" onClick={e => {
                  const input = e.target.previousElementSibling;
                  const val = input?.value?.trim();
                  if (val) { onUpdate(idx, 'options', [...(q.options || []), val]); input.value = ''; }
                }} className="px-2 py-1 text-xs bg-violet-100 text-violet-700 rounded hover:bg-violet-200">+</button>
              </div>
            </div>
          )}
        </div>

        <button onClick={() => onRemove(idx)} className="text-gray-300 hover:text-red-500 mt-1"><X size={14} /></button>
      </div>
    </div>
  );
};

// ─── Prévisualisation ─────────────────────────────────────────────────────────
const PreviewModal = ({ form, isOpen, onClose }) => (
  <Modal isOpen={isOpen} onClose={onClose} title={`Preview — ${form.name}`} size="lg">
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <ClipboardList size={16} className="text-violet-500" />
        <span>{ROUND_LABELS[form.round]}</span>
        <span>·</span>
        <span>{form.questions.filter(q => q.type !== 'section').length} questions</span>
        <span>·</span>
        <span>{form.questions.filter(q => q.type === 'section').length} sections</span>
      </div>
      <div className="space-y-3">
        {form.questions.filter(q => q.label.trim()).map((q, idx) => (
          q.type === 'section' ? (
            <div key={q.id} className="border-b-2 border-indigo-200 pb-1 pt-3">
              <h4 className="text-sm font-bold text-indigo-700 uppercase tracking-wider">{q.label}</h4>
              {q.description?.trim() && <p className="text-xs text-gray-500 mt-1 italic">{q.description}</p>}
              {q.duration && <span className="text-[10px] text-indigo-400 flex items-center gap-1 mt-1"><Clock size={10} /> {q.duration}</span>}
            </div>
          ) : (
            <div key={q.id} className="bg-gray-50 rounded-lg p-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {q.label} {q.required && <span className="text-red-400">*</span>}
              </label>
              {q.description?.trim() && <p className="text-xs text-gray-400 mb-2 italic">{q.description}</p>}
              {q.type === 'rating' ? (
                <div className="flex gap-1">{[1,2,3,4,5].map(n => <Star key={n} size={18} className="text-gray-300" />)}</div>
              ) : q.type === 'radio' ? (
                <div className="space-y-1">{(q.options||[]).map((o,i) => <label key={i} className="flex items-center gap-2 text-sm"><input type="radio" disabled />{o}</label>)}</div>
              ) : q.type === 'checkbox' ? (
                <div className="space-y-1">{(q.options||[]).map((o,i) => <label key={i} className="flex items-center gap-2 text-sm"><input type="checkbox" disabled />{o}</label>)}</div>
              ) : q.type === 'textarea' ? (
                <div className="w-full border rounded-lg px-3 py-2 h-16 bg-white text-gray-300 text-sm">Reponse longue...</div>
              ) : (
                <div className="w-full border rounded-lg px-3 py-2 bg-white text-gray-300 text-sm">Reponse courte...</div>
              )}
            </div>
          )
        ))}
      </div>
    </div>
  </Modal>
);

// ─── Composant principal ──────────────────────────────────────────────────────
const InterviewTemplateManagement = () => {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', round: 1, questions: [emptyQuestion()], isDefault: false });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const loadTemplates = async () => {
    try { const { data } = await getInterviewTemplates(); setTemplates(data); } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadTemplates(); }, []);

  const handleSave = async () => {
    const validQuestions = form.questions.filter(q => q.label.trim());
    if (!form.name.trim() || validQuestions.length === 0) { toast.error('Nom et au moins une question requis'); return; }
    try {
      const payload = { ...form, questions: validQuestions };
      if (editing) { await updateInterviewTemplate(editing.id, payload); toast.success('Template mis a jour'); }
      else { await createInterviewTemplate(payload); toast.success('Template cree'); }
      setShowModal(false); setEditing(null); loadTemplates();
    } catch (e) { toast.error('Erreur sauvegarde'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    try { await deleteInterviewTemplate(id); toast.success('Template supprime'); loadTemplates(); } catch (e) { toast.error('Erreur suppression'); }
  };

  const handleDuplicate = async (tpl) => {
    try {
      await createInterviewTemplate({ name: `${tpl.name} (copie)`, round: tpl.round, questions: tpl.questions, isDefault: false });
      toast.success('Template duplique');
      loadTemplates();
    } catch (e) { toast.error('Erreur duplication'); }
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setForm({ name: tpl.name, round: tpl.round, questions: (tpl.questions || [emptyQuestion()]).map(q => ({ ...q, description: q.description || '', duration: q.duration || '' })), isDefault: tpl.isDefault });
    setShowModal(true);
  };

  const addQuestion = () => setForm(f => ({ ...f, questions: [...f.questions, emptyQuestion()] }));
  const removeQuestion = (idx) => setForm(f => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }));
  const moveQuestion = (idx, direction) => setForm(f => {
    const qs = [...f.questions];
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= qs.length) return f;
    [qs[idx], qs[newIdx]] = [qs[newIdx], qs[idx]];
    return { ...f, questions: qs };
  });
  const updateQuestion = (idx, field, value) => setForm(f => ({
    ...f, questions: f.questions.map((q, i) => i === idx ? { ...q, [field]: value } : q),
  }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setForm(f => {
        const oldIdx = f.questions.findIndex(q => q.id === active.id);
        const newIdx = f.questions.findIndex(q => q.id === over.id);
        return { ...f, questions: arrayMove(f.questions, oldIdx, newIdx) };
      });
    }
  };

  // Stats du formulaire
  const questionCount = form.questions.filter(q => q.type !== 'section' && q.label.trim()).length;
  const sectionCount = form.questions.filter(q => q.type === 'section' && q.label.trim()).length;

  if (loading) return <div className="p-4 text-gray-400">Chargement...</div>;

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-700">Templates d'entretien</h2>
        <button onClick={() => { setEditing(null); setForm({ name: '', round: 1, questions: [emptyQuestion()], isDefault: false }); setShowModal(true); }}
          className="flex items-center gap-2 px-3 py-2 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700">
          <Plus size={16} /> Nouveau template
        </button>
      </div>

      <div className="space-y-3">
        {[1, 2, 3].map(round => {
          const roundTemplates = templates.filter(t => t.round === round);
          if (roundTemplates.length === 0) return null;
          return (
            <div key={round}>
              <h3 className="text-sm font-medium text-gray-500 mb-2">{ROUND_LABELS[round]}</h3>
              {roundTemplates.map(tpl => (
                <div key={tpl.id} className="bg-white border rounded-lg p-4 mb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <ClipboardList size={16} className="text-violet-500" />
                        <span className="font-medium text-gray-800">{tpl.name}</span>
                        {tpl.isDefault && <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">Par defaut</span>}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {tpl.questions?.filter(q => q.type !== 'section').length || 0} questions
                        {tpl.questions?.filter(q => q.type === 'section').length > 0 && ` · ${tpl.questions.filter(q => q.type === 'section').length} sections`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => { setForm({ name: tpl.name, round: tpl.round, questions: tpl.questions || [], isDefault: tpl.isDefault }); setShowPreview(true); }}
                        className="p-1.5 text-gray-400 hover:text-indigo-600" title="Previsualiser"><Eye size={14} /></button>
                      <button onClick={() => handleDuplicate(tpl)} className="p-1.5 text-gray-400 hover:text-amber-600" title="Dupliquer"><Copy size={14} /></button>
                      <button onClick={() => openEdit(tpl)} className="p-1.5 text-gray-400 hover:text-blue-600" title="Modifier"><Edit size={14} /></button>
                      <button onClick={() => handleDelete(tpl.id)} className="p-1.5 text-gray-400 hover:text-red-600" title="Supprimer"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
        {templates.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">Aucun template d'entretien. Creez-en un pour structurer vos entretiens.</p>
        )}
      </div>

      {/* Modal édition */}
      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setEditing(null); }} title={editing ? 'Modifier le template' : 'Nouveau template'} size="lg" preventClose={true}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Entretien telephonique APE" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Round *</label>
              <select value={form.round} onChange={e => setForm(f => ({ ...f, round: parseInt(e.target.value) }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                {Object.entries(ROUND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isDefault} onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))} />
              Template par defaut pour ce round
            </label>
            <div className="text-xs text-gray-400">
              {sectionCount > 0 && <span>{sectionCount} section{sectionCount > 1 ? 's' : ''} · </span>}
              {questionCount} question{questionCount > 1 ? 's' : ''}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Questions <span className="text-xs text-gray-400">(glisser-deposer pour reorganiser)</span></label>
              <div className="flex gap-2">
                <button onClick={() => setShowPreview(true)} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"><Eye size={13} /> Preview</button>
                <button onClick={addQuestion} className="text-xs text-violet-600 hover:text-violet-800 flex items-center gap-1"><Plus size={14} /> Ajouter</button>
              </div>
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={form.questions.map(q => q.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {form.questions.map((q, idx) => (
                    <SortableQuestion key={q.id} q={q} idx={idx} total={form.questions.length}
                      onUpdate={updateQuestion} onRemove={removeQuestion} onMove={moveQuestion} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => { setShowModal(false); setEditing(null); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
            <button onClick={handleSave} disabled={!form.name.trim()} className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50">
              {editing ? 'Enregistrer' : 'Creer'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal prévisualisation */}
      <PreviewModal form={form} isOpen={showPreview} onClose={() => setShowPreview(false)} />
    </div>
  );
};

export default InterviewTemplateManagement;
